import React from 'react'
import { ChevronRightIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	ModalBody,
	ModalClose,
	ModalFooter,
} from '@/shared/components/ui/modal'
import { readFileHeaders } from '@/shared/lib/download-xlsx'
import { zodResolver } from '@/shared/lib/validator/zod'

import { FormProvider, RHFCheckbox } from '../HookForm'
import RHFFileDropArea from '../HookForm/RHFFileDropArea'
import { useImportFromFile } from './ImportFromFileProvider'

const importFromFileSchema = z.object({
	product_diffs: z
		.array(
			z
				.instanceof(File)
				.refine((file) =>
					['xlsx', 'xls', 'csv'].includes(file?.name?.split('.').pop() || '')
				)
		)
		.min(1),
	ignore_empties: z.boolean().default(true),
})

type ImportFromFileFormData = z.infer<typeof importFromFileSchema>

const ImportFromFileForm = ({ children }: { children: React.ReactNode }) => {
	const { t } = useTranslation()

	const context = useImportFromFile()
	const methods = useForm({
		defaultValues: context.draft,
		resolver: zodResolver(importFromFileSchema),
	})

	const handleSubmit = methods.handleSubmit(async (data) => {
		context.setDraft(data)
		const files = data.product_diffs as File[]
		if (files && files.length > 0) {
			const headers = (await readFileHeaders(files[0])) as string[]
			if (headers) {
				context.setHeaders(headers)
				context.setStep('mapping')
			}
		}
	})

	return (
		<>
			<ModalBody className='relative flex flex-col'>
				<FormProvider
					methods={methods}
					onSubmit={handleSubmit}
					className='flex flex-col gap-3'
				>
					{children}
				</FormProvider>
			</ModalBody>
			<ModalFooter className='justify-between gap-2'>
				<ModalClose asChild className='mr-auto'>
					<Button>{t('general_cancel')}</Button>
				</ModalClose>
				<Button variant='primary' onClick={handleSubmit}>
					{t('general_next')}
					<ChevronRightIcon />
				</Button>
			</ModalFooter>
		</>
	)
}

const ImportFromFileFormFields = () => {
	const { t } = useTranslation()

	return (
		<>
			<RHFFileDropArea name='product_diffs' />
			<RHFCheckbox
				name='ignore_empties'
				label={t('modal_import_ignore_empties')}
			/>
		</>
	)
}
const ImportFromFileFormDescription = ({
	onDownloadTemplate,
}: {
	onDownloadTemplate: () => void
}) => {
	const { t } = useTranslation()

	return (
		<div className='prose-xs prose text-xs prose-neutral dark:prose-invert'>
			<h5>{t('modal_import_guide')}</h5>
			<ul className='mb-4 list-decimal text-muted-foreground'>
				<li>
					{t('og_import_step_1')}
					<LinkButton
						size='sm'
						variant='brand'
						onClick={onDownloadTemplate}
						className='ml-1'
					>
						{t('og_import_types')}
					</LinkButton>
				</li>
				<li>{t('og_import_step_2')}</li>
				<li>{t('og_import_step_3')}</li>
			</ul>
		</div>
	)
}
export {
	ImportFromFileForm,
	ImportFromFileFormFields,
	ImportFromFileFormDescription,
	importFromFileSchema,
	type ImportFromFileFormData,
}
